import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ActivityIndicator, Alert, Text, View } from 'react-native';
import { useIsFocused } from 'expo-router';
import { trpc } from '../lib/trpc';
import { formatRelativeTime } from '../lib/utils';
import { usePlaidConnection } from '../hooks/usePlaidConnection';
import { useSnapTradeConnection } from '../hooks/useSnapTradeConnection';
import { AccountConnectionActions } from './AccountConnectionActions';
import { AccountTaxSubtypeReviewSheet } from './AccountTaxSubtypeReviewSheet';
import { AddManualAccountSheet } from './AddManualAccountSheet';
import { Badge } from './Badge';
import { Card } from './Card';
import { ConnectionAccountRows } from './ConnectionAccountRows';
import { InstitutionLogo } from './InstitutionLogo';
import {
  MortgagePlanningReviewSheet,
  mortgageNeedsPlanningReview,
} from './MortgagePlanningReviewSheet';
import { SectionHeader } from './SectionHeader';
import { SnapTradePortal } from './SnapTradePortal';
import { useConfirmationDialog } from './ConfirmationDialog';

const STALE_THRESHOLD_MS = 24 * 60 * 60 * 1000;
const PLAID_ACCOUNT_SELECTION_ERROR_CODE = 'PLAID_ACCOUNT_SELECTION_REQUIRED';

function isConnectionStale(lastSyncedAt: string | null): boolean {
  if (!lastSyncedAt) return true;
  return Date.now() - new Date(lastSyncedAt).getTime() > STALE_THRESHOLD_MS;
}

function formatOmittedAccountNames(
  accounts: Array<{ name: string; mask: string | null }>,
): string | null {
  if (accounts.length === 0) return null;
  const names = accounts
    .slice(0, 2)
    .map((account) => account.mask ? `${account.name} (${account.mask})` : account.name);
  return names.join(', ') + (accounts.length > 2 ? ` +${accounts.length - 2}` : '');
}

export function AccountConnectionsManager({ isDemo = false }: { isDemo?: boolean }) {
  const isFocused = useIsFocused();
  const utils = trpc.useUtils();
  const { confirm } = useConfirmationDialog();
  const [syncingConnectionId, setSyncingConnectionId] = useState<string | null>(null);
  const [pendingReviewConnectionId, setPendingReviewConnectionId] = useState<string | null>(null);
  const [reviewSheetAccountIds, setReviewSheetAccountIds] = useState<string[]>([]);
  const [mortgageReviewLiabilityIds, setMortgageReviewLiabilityIds] = useState<string[]>([]);
  const [reconnectingPlaidConnectionId, setReconnectingPlaidConnectionId] = useState<string | null>(null);
  const [manualSheetOpen, setManualSheetOpen] = useState(false);
  const [manualSheetConnectionId, setManualSheetConnectionId] = useState<string | null>(null);
  const pendingPlaidReconnect = useRef(false);
  const mortgageReviewDismissedRef = useRef(false);

  const accountsQ = trpc.accounts.list.useQuery(undefined, { enabled: isFocused });
  const liabilitiesQ = trpc.liabilities.list.useQuery({ type: 'mortgage' }, { enabled: isFocused });
  const connectionsQ = trpc.connections.list.useQuery(undefined, {
    enabled: isFocused,
    refetchInterval: (query) => {
      const isSyncing = query.state.data?.some((c) => c.status === 'syncing');
      return isSyncing ? 2000 : false;
    },
  });
  const pendingReviewAccountsQ = trpc.accounts.list.useQuery(
    pendingReviewConnectionId
      ? { connectionId: pendingReviewConnectionId, needsTaxSubtypeReview: true }
      : { needsTaxSubtypeReview: true },
    {
      enabled: !!pendingReviewConnectionId,
      refetchInterval: (query) => {
        if (!pendingReviewConnectionId) return false;
        const connection = connectionsQ.data?.find((item) => item.id === pendingReviewConnectionId);
        if ((query.state.data?.length ?? 0) > 0) return false;
        return connection?.status === 'syncing' ? 2000 : false;
      },
    },
  );

  const deleteConnectionMutation = trpc.connections.delete.useMutation();
  const syncConnectionMutation = trpc.connections.sync.useMutation();

  const refetchConnectionData = useCallback(async () => {
    await Promise.allSettled([
      accountsQ.refetch(),
      liabilitiesQ.refetch(),
      connectionsQ.refetch(),
      utils.accounts.getBalancesByTaxType.invalidate(),
      utils.accounts.getMetrics.invalidate(),
      utils.fire.getPlan.invalidate(),
      utils.fire.getRecalcStatus.invalidate(),
    ]);
  }, [
    accountsQ,
    connectionsQ,
    liabilitiesQ,
    utils.accounts.getBalancesByTaxType,
    utils.accounts.getMetrics,
    utils.fire.getPlan,
    utils.fire.getRecalcStatus,
  ]);

  const plaid = usePlaidConnection({
    onSuccess: async (connectionId) => {
      setPendingReviewConnectionId(connectionId);
      await refetchConnectionData();
    },
    onError: (err) => Alert.alert('Connection Error', err),
  });

  const plaidReconnect = usePlaidConnection({
    connectionId: reconnectingPlaidConnectionId ?? undefined,
    onSuccess: async (connectionId) => {
      setPendingReviewConnectionId(connectionId);
      setReconnectingPlaidConnectionId(null);
      await refetchConnectionData();
    },
    onError: (err) => {
      setReconnectingPlaidConnectionId(null);
      Alert.alert('Connection Error', err);
    },
  });

  const snaptrade = useSnapTradeConnection({
    onSuccess: async (connectionId) => {
      setPendingReviewConnectionId(connectionId);
      await refetchConnectionData();
    },
    onError: (err) => Alert.alert('Connection Error', err),
  });

  useEffect(() => {
    if (!pendingReviewConnectionId || reviewSheetAccountIds.length > 0) return;

    const connection = connectionsQ.data?.find((item) => item.id === pendingReviewConnectionId);
    const pendingAccounts = pendingReviewAccountsQ.data ?? [];

    if (pendingAccounts.length > 0) {
      setReviewSheetAccountIds(pendingAccounts.map((account) => account.id));
      return;
    }

    if (connection && connection.status !== 'syncing') {
      setPendingReviewConnectionId(null);
    }
  }, [
    connectionsQ.data,
    pendingReviewAccountsQ.data,
    pendingReviewConnectionId,
    reviewSheetAccountIds.length,
  ]);

  useEffect(() => {
    if (!pendingPlaidReconnect.current || !reconnectingPlaidConnectionId) return;

    pendingPlaidReconnect.current = false;
    plaidReconnect.open();
  }, [plaidReconnect.open, reconnectingPlaidConnectionId]);

  const liabilities = liabilitiesQ.data ?? [];
  const mortgageLiabilitiesNeedingReview = useMemo(
    () => liabilities.filter(mortgageNeedsPlanningReview),
    [liabilities],
  );

  useEffect(() => {
    if (mortgageReviewDismissedRef.current) return;
    if (reviewSheetAccountIds.length > 0) return;
    if (mortgageReviewLiabilityIds.length > 0) return;
    if (mortgageLiabilitiesNeedingReview.length === 0) return;

    setMortgageReviewLiabilityIds(
      mortgageLiabilitiesNeedingReview.map((liability) => liability.id),
    );
  }, [
    mortgageLiabilitiesNeedingReview,
    mortgageReviewLiabilityIds.length,
    reviewSheetAccountIds.length,
  ]);

  const handleDeleteConnection = useCallback(
    async (connId: string, name: string) => {
      const choice = await confirm({
        title: 'Remove Connection',
        message: `Choose what to remove for ${name}.`,
        actions: [
          { key: 'cancel', label: 'Cancel', role: 'cancel' },
          {
            key: 'disconnect',
            label: 'Disconnect Only',
            description: 'Keep imported accounts and transaction history in Per Diem, but stop future syncing.',
          },
          {
            key: 'purge',
            label: 'Disconnect and Delete Data',
            description: 'Remove imported accounts, transactions, holdings, and linked liabilities from this connection.',
            role: 'destructive',
          },
        ],
      });

      if (choice !== 'disconnect' && choice !== 'purge') {
        return;
      }

      try {
        await deleteConnectionMutation.mutateAsync({
          id: connId,
          ...(choice === 'purge' ? { purgeData: true } : {}),
        });
        await refetchConnectionData();
      } catch {
        Alert.alert('Error', 'Failed to remove connection. Try again.');
      }
    },
    [confirm, deleteConnectionMutation, refetchConnectionData],
  );

  const handleSyncConnection = useCallback(
    async (connId: string) => {
      try {
        setSyncingConnectionId(connId);
        await syncConnectionMutation.mutateAsync({ id: connId });
        await refetchConnectionData();
      } catch {
        Alert.alert('Error', 'Sync failed. Try again.');
      } finally {
        setSyncingConnectionId(null);
      }
    },
    [syncConnectionMutation, refetchConnectionData],
  );

  const handleReconnectPlaidConnection = useCallback((connectionId: string) => {
    pendingPlaidReconnect.current = true;
    setReconnectingPlaidConnectionId(connectionId);
  }, []);

  const closeTaxSubtypeReview = () => {
    setReviewSheetAccountIds([]);
    setPendingReviewConnectionId(null);
  };

  const handleTaxSubtypeSaved = async () => {
    await refetchConnectionData();
    setReviewSheetAccountIds([]);
    setPendingReviewConnectionId(null);
  };

  const closeMortgageReview = () => {
    mortgageReviewDismissedRef.current = true;
    setMortgageReviewLiabilityIds([]);
  };

  const handleMortgagePlanningSaved = async (liabilityId: string) => {
    await refetchConnectionData();
    setMortgageReviewLiabilityIds((current) =>
      current.filter((id) => id !== liabilityId),
    );
  };

  const renderAddConnectionActions = () => (
    <AccountConnectionActions
      onConnectAccount={plaid.open}
      connectAccountLoading={plaid.isLoading}
      onAddManualAccount={() => {
        setManualSheetConnectionId(null);
        setManualSheetOpen(true);
      }}
      onConnectBrokerage={snaptrade.open}
      connectBrokerageLoading={snaptrade.isLoading}
      showBrokerageFallback={false}
      disabled={isDemo || plaid.isLoading || snaptrade.isLoading}
    />
  );

  const accounts = accountsQ.data ?? [];
  const connections = connectionsQ.data ?? [];
  const reviewAccountSource = [
    ...(pendingReviewAccountsQ.data ?? []),
    ...accounts,
  ].filter(
    (account, index, all) => all.findIndex((candidate) => candidate.id === account.id) === index,
  );
  const reviewSheetAccounts = reviewAccountSource.filter((account) =>
    reviewSheetAccountIds.includes(account.id),
  );
  const mortgageReviewLiabilities = liabilities.filter((liability) =>
    mortgageReviewLiabilityIds.includes(liability.id),
  );

  const syncedConnections = connections.filter((c) => c.provider !== 'manual');
  const institutionCounts = new Map<string, number>();
  syncedConnections.forEach((c) => {
    const key = c.institutionName ?? '';
    institutionCounts.set(key, (institutionCounts.get(key) ?? 0) + 1);
  });

  const accountsByConnection = useMemo(() => {
    const next = new Map<string, typeof accounts>();
    accounts.forEach((account) => {
      if (!account.connectionId) return;
      const list = next.get(account.connectionId) ?? [];
      list.push(account);
      next.set(account.connectionId, list);
    });
    return next;
  }, [accounts]);
  const liabilitiesByAccountId = useMemo(
    () => new Map(liabilities.map((liability) => [liability.accountId, liability] as const)),
    [liabilities],
  );

  return (
    <>
      <SnapTradePortal
        loginLink={snaptrade.oauthUrl}
        isOpen={snaptrade.isOpen}
        onClose={snaptrade.close}
        onSuccess={snaptrade.handleSuccess}
        onError={(err) => Alert.alert('Connection Error', err)}
      />
      <AccountTaxSubtypeReviewSheet
        visible={reviewSheetAccounts.length > 0}
        accounts={reviewSheetAccounts}
        onClose={closeTaxSubtypeReview}
        onSaved={handleTaxSubtypeSaved}
      />
      <MortgagePlanningReviewSheet
        visible={reviewSheetAccounts.length === 0 && mortgageReviewLiabilities.length > 0}
        liabilities={mortgageReviewLiabilities}
        onClose={closeMortgageReview}
        onSaved={handleMortgagePlanningSaved}
      />
      <AddManualAccountSheet
        visible={manualSheetOpen}
        manualConnectionId={manualSheetConnectionId}
        onClose={() => {
          setManualSheetOpen(false);
          setManualSheetConnectionId(null);
        }}
        onCreated={async () => {
          await refetchConnectionData();
        }}
      />

      <Card>
        <SectionHeader title="Account Connections" />
        <Text className="mb-3 text-sm leading-5 text-gray-600 dark:text-gray-300">
          Connect banks, brokerages, and manually tracked institutions that feed your balance sheet,
          transactions, budget, and FIRE plan.
        </Text>

        {isDemo ? (
          <View className="mb-3 rounded-xl bg-amber-50 px-4 py-3 dark:bg-amber-950">
            <Text className="text-sm font-medium text-amber-900 dark:text-amber-100">
              Demo accounts are read-only. Account linking and sync actions are disabled.
            </Text>
          </View>
        ) : null}

        {connectionsQ.isLoading || accountsQ.isLoading || liabilitiesQ.isLoading ? (
          <View className="items-center py-6">
            <ActivityIndicator />
            <Text className="mt-2 text-sm text-gray-500 dark:text-gray-400">Loading connections...</Text>
          </View>
        ) : connectionsQ.error || accountsQ.error || liabilitiesQ.error ? (
          <View className="rounded-xl bg-red-50 px-4 py-3 dark:bg-red-950">
            <Text className="text-sm text-red-700 dark:text-red-100">
              Could not load account connections. Try again in a moment.
            </Text>
          </View>
        ) : connections.length > 0 ? (
          <View>
            {connections.map((connection) => {
              const isManualConnection = connection.provider === 'manual';
              const isPlaidReconnecting =
                reconnectingPlaidConnectionId === connection.id && plaidReconnect.isLoading;
              const connAccounts = accountsByConnection.get(connection.id) ?? [];
              const omittedAccounts = connAccounts.filter(
                (account) => account.providerOmittedSinceLastSync,
              );
              const omittedAccountNames = formatOmittedAccountNames(omittedAccounts);
              const needsPlaidAccountSelection =
                connection.errorCode === PLAID_ACCOUNT_SELECTION_ERROR_CODE;
              const isDuplicate =
                !isManualConnection &&
                (institutionCounts.get(connection.institutionName ?? '') ?? 0) > 1;
              const accountNameSubtitle = isDuplicate
                ? connAccounts
                    .slice(0, 2)
                    .map((a) => a.name)
                    .join(', ') +
                  (connAccounts.length > 2 ? ` +${connAccounts.length - 2}` : '')
                : null;
              const statusLabel =
                needsPlaidAccountSelection
                  ? 'Needs action'
                  : connection.status === 'active' && isConnectionStale(connection.lastSyncedAt)
                    ? 'Stale'
                    : connection.status === 'active'
                      ? 'Active'
                      : connection.status === 'syncing'
                        ? 'Syncing...'
                        : connection.status;
              const providerMetaLines = connection.lastSyncedAt
                ? [
                    connection.provider,
                    `Last synced ${formatRelativeTime(connection.lastSyncedAt)}`,
                  ]
                : [connection.provider];

              return (
                <View
                  key={connection.id}
                  className="gap-3 border-b border-gray-50 py-3 last:border-b-0 dark:border-gray-700"
                >
                  <View className="flex-row items-center gap-3">
                    <InstitutionLogo
                      logoBase64={connection.institutionLogoBase64}
                      name={connection.institutionName ?? 'Bank'}
                      size={36}
                    />
                    <View className="min-w-0 flex-1">
                      <Text className="text-sm font-medium text-gray-800 dark:text-gray-200">
                        {connection.institutionName ?? 'Unknown'}
                      </Text>
                      {accountNameSubtitle ? (
                        <Text className="text-xs text-gray-500 dark:text-gray-400" numberOfLines={1}>
                          {accountNameSubtitle}
                        </Text>
                      ) : null}
                      {isManualConnection ? (
                        <Text className="text-xs text-gray-400">
                          {connAccounts.length} manual account{connAccounts.length === 1 ? '' : 's'}
                        </Text>
                      ) : (
                        <View className="gap-0.5">
                          {providerMetaLines.map((line) => (
                            <Text key={line} className="text-xs text-gray-400">
                              {line}
                            </Text>
                          ))}
                          {needsPlaidAccountSelection ? (
                            <Text className="mt-0.5 text-xs text-yellow-600 dark:text-yellow-400">
                              {omittedAccountNames
                                ? `Reconnect to refresh ${omittedAccountNames}`
                                : 'Reconnect to choose shared accounts'}
                            </Text>
                          ) : null}
                          {!needsPlaidAccountSelection
                          && connection.status === 'active'
                          && isConnectionStale(connection.lastSyncedAt) ? (
                            <Text className="mt-0.5 text-xs text-yellow-600 dark:text-yellow-400">
                              Hasn't synced recently
                            </Text>
                          ) : null}
                        </View>
                      )}
                    </View>
                    <View className="items-end gap-2">
                      {!isManualConnection ? (
                        <Badge
                          label={statusLabel}
                          variant={
                            needsPlaidAccountSelection
                              ? 'yellow'
                              : connection.status === 'active' && isConnectionStale(connection.lastSyncedAt)
                                ? 'yellow'
                                : connection.status === 'active'
                                  ? 'green'
                                  : connection.status === 'syncing'
                                    ? 'yellow'
                                    : 'red'
                          }
                        />
                      ) : null}
                      <View className="flex-row flex-wrap justify-end gap-2">
                        {isManualConnection ? (
                          <Text
                            className={`text-xs ${isDemo ? 'text-gray-400' : 'text-blue-500'}`}
                            onPress={
                              isDemo
                                ? undefined
                                : () => {
                                    setManualSheetConnectionId(connection.id);
                                    setManualSheetOpen(true);
                                  }
                            }
                          >
                            Add account
                          </Text>
                        ) : connection.status === 'syncing' || syncingConnectionId === connection.id ? (
                          <Text className="text-xs text-gray-400">Syncing...</Text>
                        ) : (
                          <Text
                            className={`text-xs ${isDemo ? 'text-gray-400' : 'text-blue-500'}`}
                            onPress={
                              isDemo
                                ? undefined
                                : () => handleSyncConnection(connection.id)
                            }
                          >
                            Sync
                          </Text>
                        )}
                        {!isManualConnection && connection.provider === 'plaid' ? (
                          <Text
                            className={`text-xs ${
                              isDemo || isPlaidReconnecting ? 'text-gray-400' : 'text-blue-500'
                            }`}
                            onPress={
                              isDemo || isPlaidReconnecting || syncingConnectionId === connection.id
                                ? undefined
                                : () => handleReconnectPlaidConnection(connection.id)
                            }
                          >
                            {isPlaidReconnecting
                              ? 'Opening...'
                              : needsPlaidAccountSelection
                                ? 'Select accounts'
                                : 'Reconnect'}
                          </Text>
                        ) : null}
                        <Text
                          className={`text-xs ${isDemo ? 'text-gray-400' : 'text-red-400'}`}
                          onPress={
                            isDemo
                              ? undefined
                              : () => handleDeleteConnection(
                                  connection.id,
                                  connection.institutionName ?? 'account',
                                )
                          }
                        >
                          Remove
                        </Text>
                      </View>
                    </View>
                  </View>
                  <ConnectionAccountRows
                    accounts={connAccounts}
                    liabilitiesByAccountId={liabilitiesByAccountId}
                    onReviewTaxAccount={(accountId) => setReviewSheetAccountIds([accountId])}
                    onReviewMortgage={(liabilityId) => {
                      mortgageReviewDismissedRef.current = false;
                      setMortgageReviewLiabilityIds([liabilityId]);
                    }}
                    className="pl-12"
                  />
                </View>
              );
            })}
            {renderAddConnectionActions()}
          </View>
        ) : (
          <View>
            <Text className="text-sm text-gray-500 dark:text-gray-400">
              Link a financial institution or add a manual account to start syncing balances and
              transactions into Per Diem.
            </Text>
            {renderAddConnectionActions()}
          </View>
        )}
      </Card>
    </>
  );
}
